export const KEY = "loancontrol.copy.demo.v1";
export const CSV_HEADER = "receipt_id,case_id,disbursement_id,bank,amount,date";
export const BANKS = ["HDFC Bank", "ICICI Bank", "Axis Bank", "SBI"];
export const COLLECTION_TYPES = ["Partial bank receipt", "Overdue bank commission"];

export const money = n => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0
}).format(n);

export const sum = (items, fn) => items.reduce((n, item) => n + fn(item), 0);
export const round = n => Math.round(n * 100) / 100;
export const uid = prefix => prefix + "-" + crypto.randomUUID().slice(0, 8).toUpperCase();

export function today() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(new Date());
  const value = type => parts.find(p => p.type === type).value;
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function offset(days) {
  const date = new Date(today() + "T12:00:00Z");
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function initialState() {
  const partners = [
    { id: "DSA-101", name: "Aster Home Finance", city: "Bengaluru", limit: 150000 },
    { id: "DSA-102", name: "Harbor Loan Partners", city: "Mumbai", limit: 120000 },
    { id: "DSA-103", name: "Northgate Financial", city: "Delhi NCR", limit: 150000 },
    { id: "DSA-104", name: "Deccan Credit Desk", city: "Hyderabad", limit: 20000 }
  ];
  const definitions = [
    ["LC-1001", "Ananya Rao", 0, "HDFC Bank", "Bengaluru", 5000000, -25, true, 50000, 28000],
    ["LC-1002", "Rohan Mehta", 1, "ICICI Bank", "Mumbai", 8000000, -20, true, 50000, 44800],
    ["LC-1003", "Priya Sharma", 2, "Axis Bank", "Delhi NCR", 6000000, -50, true, 0, 33600],
    ["LC-1004", "Vikram Reddy", 3, "SBI", "Hyderabad", 7000000, -6, true, 0, 0],
    ["LC-1005", "Neha Iyer", 0, "HDFC Bank", "Chennai", 4500000, -3, true, 0, 0],
    ["LC-1006", "Kabir Joshi", 1, "ICICI Bank", "Pune", 3500000, -1, false, 0, 0]
  ];
  const cases = definitions.map(row => ({
    id: row[0], name: row[1], partner: partners[row[2]].id, bank: row[3], city: row[4],
    amount: row[5], date: offset(row[6]), due: offset(row[6] + 30), verified: row[7],
    disb: "TR-" + row[0].slice(3), ref: "BANK-" + row[0],
    bankRate: 1, dsaRate: 0.7, advancePct: 80, agreement: "DEMO-V1"
  }));
  const result = {
    version: 1, partners, cases, payments: [], receipts: [], exceptions: [], audit: [], limitHistory: [], bankLimit: 500000
  };
  definitions.forEach((row, index) => {
    const c = cases[index];
    if (row[8]) result.receipts.push({
      id: "RCPT-" + index, external: "DEMO-UTR-" + index, caseId: c.id, disb: c.disb,
      bank: c.bank, amount: row[8], date: today()
    });
    if (row[9]) result.payments.push({
      id: "PAY-" + index, caseId: c.id, disb: c.disb, amount: row[9], status: "Paid",
      maker: "maker", checker: "checker", date: today()
    });
  });
  result.exceptions = [
    {
      id: "EX-DUP", caseId: "LC-1001", disb: "TR-1001", type: "Duplicate payout attempt",
      amount: 28000, owner: "Operations controls", due: offset(1), status: "Open",
      note: "A second advance was blocked. No duplicate payment was made."
    },
    {
      id: "EX-LIMIT", caseId: "LC-1004", disb: "TR-1004", type: "Exposure limit breach",
      amount: 39200, owner: "Risk and Finance", due: offset(2), status: "Open",
      note: "Proposed advance exceeds the partner limit of ₹20,000."
    }
  ];
  return result;
}

export function loadState() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (s && s.version === 1) return s;
  } catch {
    // fall through to fresh data
  }
  return initialState();
}

export function saveState(s) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
    return true;
  } catch {
    return false;
  }
}

export const getCase = (s, id) => s.cases.find(c => c.id === id);
export const getPartner = (s, c) => s.partners.find(p => p.id === c.partner);
export const expected = c => (c.verified ? round(c.amount * c.bankRate / 100) : 0);
export const entitlement = c => (c.verified ? round(c.amount * c.dsaRate / 100) : 0);
export const advance = c => round(entitlement(c) * c.advancePct / 100);
export const received = (s, c) => round(sum(s.receipts.filter(r => r.caseId === c.id), r => r.amount));
export const paid = (s, c) =>
  round(sum(s.payments.filter(p => p.caseId === c.id && p.status === "Paid"), p => p.amount));
export const outstanding = (s, c) => Math.max(0, round(expected(c) - received(s, c)));
export const pending = (s, c) => s.payments.find(p => p.caseId === c.id && p.status === "Pending");
export const exposure = (s, c) => (outstanding(s, c) > 0 ? paid(s, c) : 0);
export const reserved = (s, c) =>
  sum(s.payments.filter(p => p.caseId === c.id && p.status === "Pending"), p => p.amount);
export const partnerExposure = (s, partner) =>
  sum(s.cases.filter(c => c.partner === partner.id), c => exposure(s, c) + reserved(s, c));
export const bankExposure = (s, bank) =>
  sum(s.cases.filter(c => c.bank === bank), c => exposure(s, c) + reserved(s, c));

export function checks(s, c, existingReservation = false) {
  const partner = getPartner(s, c);
  const extra = existingReservation ? 0 : advance(c);
  return [
    ["Disbursement verified", c.verified],
    ["Valid approved agreement and rates",
      !!c.agreement && c.bankRate > 0 && c.dsaRate >= 0 && c.dsaRate <= c.bankRate],
    ["Advance not already paid", paid(s, c) === 0],
    ["No other pending advance", existingReservation || !pending(s, c)],
    [`Partner exposure within ${money(partner.limit)}`, partnerExposure(s, partner) + extra <= partner.limit],
    [`Bank exposure within ${money(s.bankLimit)}`, bankExposure(s, c.bank) + extra <= s.bankLimit]
  ];
}

export function collectionStatus(s, c) {
  if (!c.verified) return "Verification pending";
  if (outstanding(s, c) === 0) return "Matched";
  if (received(s, c) > 0) return "Partial receipt";
  if (c.due < today()) return "Overdue";
  return "Awaiting bank";
}

export function audit(s, actor, action, c, amount = 0) {
  s.audit.unshift({
    time: new Date().toISOString(), actor, action,
    caseId: c?.id || "", disb: c?.disb || "", amount
  });
}

export function addException(s, c, type, amount, note) {
  const item = s.exceptions.find(e => e.caseId === c.id && e.type === type && e.status === "Open");
  if (item) {
    item.amount = amount;
    item.note = note;
    return;
  }
  s.exceptions.push({
    id: uid("EX"), caseId: c.id, disb: c.disb, type, amount,
    owner: /Exposure/.test(type) ? "Risk and Finance"
      : /Duplicate|eligibility/.test(type) ? "Operations controls" : "Finance reconciliation",
    due: offset(3), status: "Open", note
  });
}

export function syncExceptions(s) {
  s.cases.forEach(c => {
    if (received(s, c) > 0 && outstanding(s, c) > 0) {
      addException(s, c, "Partial bank receipt", outstanding(s, c), "Investigate the remaining bank commission.");
    }
    if (c.verified && c.due < today() && outstanding(s, c) > 0) {
      addException(s, c, "Overdue bank commission", outstanding(s, c), "Bank payment overdue. Follow up on collection.");
    }
    s.exceptions
      .filter(e => e.caseId === c.id && COLLECTION_TYPES.includes(e.type))
      .forEach(e => {
        e.amount = outstanding(s, c);
        if (!outstanding(s, c)) {
          e.status = "Resolved";
          e.note = "Resolved through full bank receipt matching.";
        }
      });
  });
}

export const badgeClass = text =>
  /blocked|breach|Duplicate|Overdue/i.test(text) ? "bad"
    : /pending|Partial|Awaiting|Open/i.test(text) ? "warn"
      : text === "Resolved" ? "neutral" : "";

export function visibleCases(s, search, region) {
  return s.cases.filter(c => {
    const text = [c.id, c.name, c.bank, c.city, c.ref, getPartner(s, c).name].join(" ").toLowerCase();
    return text.includes(search.toLowerCase()) && (region === "All regions" || c.city === region);
  });
}

const csvCell = v => {
  const t = String(v ?? "");
  return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

export function auditCsv(s) {
  const rows = s.audit.map(a => [
    new Date(a.time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    a.actor, a.action, a.caseId, a.disb, a.amount
  ]);
  return [["time_ist", "actor", "action", "case_id", "disbursement_id", "amount_inr"], ...rows]
    .map(r => r.map(csvCell).join(",")).join("\n");
}

export const receiptSample = () =>
  CSV_HEADER + "\n" + "DEMO-REMAINING-1002,LC-1002,TR-1002,ICICI Bank,30000," + today();

export function validateReceiptImport(s, text) {
  const lines = text.trim().split(/\r?\n/);
  const header = lines[0]?.replace(/^\uFEFF/, "").trim();
  if (header !== CSV_HEADER) throw Error("Use the exact template header.");
  if (lines.length > 5001) throw Error("Demo limit: 5,000 receipt rows.");
  const seen = new Set(s.receipts.map(r => r.external));
  const batchTotals = new Map();
  const rows = [];
  for (let index = 1; index < lines.length; index++) {
    if (!lines[index].trim()) continue;
    const fields = lines[index].split(",").map(x => x.trim());
    const fail = message => { throw Error(`Row ${index + 1}: ${message}`); };
    if (fields.length !== 6) fail("six plain fields required.");
    const [external, caseId, disb, bank, value, date] = fields;
    const amount = Number(value);
    const c = getCase(s, caseId);
    if (!external || seen.has(external)) fail("duplicate or empty receipt ID.");
    if (!c || c.disb !== disb || c.bank !== bank) fail("case, tranche and bank must match exactly.");
    if (!c.verified) fail("disbursement is not verified.");
    if (!Number.isFinite(amount) || amount <= 0 || round(amount) !== amount) {
      fail("positive amount with at most two decimals required.");
    }
    const parsed = new Date(date + "T12:00:00Z");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(parsed.getTime()) ||
      parsed.toISOString().slice(0, 10) !== date || date > today()) {
      fail("valid receipt date no later than today required.");
    }
    const total = round((batchTotals.get(caseId) || 0) + amount);
    if (total > outstanding(s, c)) fail("amount exceeds outstanding commission.");
    batchTotals.set(caseId, total);
    seen.add(external);
    rows.push({ id: uid("RCPT"), external, caseId, disb, bank, amount, date });
  }
  if (!rows.length) throw Error("Add at least one receipt.");
  return rows;
}
